import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  Typography,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Close as CloseIcon } from '@material-ui/icons'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack5'
import { useFiles, useBedFiles } from 'src/api/file'
import { START_ERRORS, startAnalyses, describeWorkflow, newBatch } from 'src/services/analysisStart'
import { useRnaVocabulary } from 'src/api/rna'
import { rnaCaptureOf } from 'src/views/AnalysisView/lib/rnaLibrary'
import { BATCHES_QUERY_KEY, batchPath } from 'src/views/Batches/useBatches'
import useAuth from 'src/hooks/useAuth'
import { ANALYSIS_STATS_KEY } from 'src/views/AnalysisView/useAnalysisStats'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import WorkflowColumn, { cnvCohortsQueryKey, fetchCnvCohorts } from './WorkflowColumn'
import EmptySamples, { recentUploads } from './EmptySamples'
import SampleEditor from './SampleEditor'
import SampleTable from './SampleTable'
import SheetImport from './SheetImport'
import FilePickerDialog, { FILE_TYPE_OF_INPUT } from './FilePickerDialog'
import OtherAnalysisTypesDialog from 'src/views/AnalysisView/OtherAnalysisTypesDialog'
import { ReviewView, StartView } from './ReviewView'
import SetAsideNote, { setAsideFiles } from './components/SetAsideNote'
import { Muted, TextLink, quietScrollSx } from './components/ui'
import useGroupFlow from './groups/useGroupFlow'
import { EMPTY_GROUP_STATE, groupStateHasWork } from './groups/model'
import { FEATURES } from './model/features'
import {
  addCandidates,
  canSavePatient,
  computeIssues,
  duplicateKeysOf,
  fileIdOf,
  fileStateOf,
  aiPatch,
  effectiveAI,
  groupRows,
  inputOfSample,
  isSingleReadRow,
  majorityInput,
  mergeSamples,
  normalFieldsOf,
  normalFileIdsInUse,
  partitionSamples,
  splitLanes,
  suggestNormal,
  withParsedAges,
} from './model/samples'
import {
  BASE_WORKFLOW,
  INPUT_OPTIONS,
  REMEMBERED_FIELDS,
  aiApplies,
  autoBatchName,
  isFileAnalysis,
  isGroupAnalysis,
  isTumourNormal,
  loadLastWorkflow,
  namesCohort,
  normaliseWorkflow,
  reconcileCohort,
  reconcileKit,
  saveLastWorkflow,
  switchWorkflow,
  validateWorkflow,
} from './model/workflow'
import { balanceOf, batchPrice, creditsText, formatCredits, usePrices, workflowUnitPrice } from './model/pricing'
import useNameWithDefault from './components/useNameWithDefault'
import SampleCredits from './components/SampleCredits'
import { analysedFileIds, candidateFitsWorkflow } from './model/candidates'
import { withSheetAdditions } from './model/sheet'
import { mergeHpo } from './model/hpo'
import {
  deleteDraft,
  draftHasWork,
  loadDraft,
  loadDrafts,
  newDraftId,
  notStartedSamples,
  restoreDraft,
  saveDraft,
} from './model/draft'
import { userKeyOf } from './model/storage'
import { RECENT_ANALYSES_KEY, RECENT_ANALYSES_OPTIONS, fetchRecentAnalyses } from './useUploadedNotAnalysed'

// ----------------------------------------------------------------------
// New analysis: one near-full-screen panel for one sample or a batch, of every
// analysis type. Germline and Somatic run on files (FASTQ, ONT BAM, PacBio BAM,
// VCF); Multisample, CNV cohort and Carrier run on groups of finished analyses, and
// their samples area, review, start and footer come from groups/useGroupFlow.
//
//   <NewAnalysisDialog open onClose onStarted initialFiles initialSamples initialWorkflow
//     initialName onOpenOtherTypes />
//
// Picked files that do not fit the batch's analysis and input are set aside, never
// dropped (model/samples partitionSamples): a line above the samples offers a batch
// of their own, and so does the progress view once a batch has run.
//
// initialFiles (optional): /file/list rows or their ids (fastq_pair_id, vcf_id,
// ont_bam_id, pacbio_bam_id) to start with.
// initialSamples (optional): candidates from the "Uploaded, not analysed yet" list
// (groupRows of /file/list), added as the list's Add does, in the given order.
// initialWorkflow (optional): a complete workflow to open with, as Home's Suggested
// card builds it (home/suggestions workflowFromParameters); it wins over the last
// workflow. initialName (optional): the batch name to open with, a value the user can
// change (nothing renames it).
//
// Drafts (model/draft): a short list per user. The panel always opens fresh, never on
// a draft; the empty samples area lists the other drafts (components/DraftList) with
// Continue, which reopens the panel on that draft, and a delete with Undo. Every
// opening is a session with its own draft id (a new one, or the continued draft's),
// and the session writes only that entry: it saves itself while the user composes,
// is saved on close ("Saved to Drafts"), and after a batch start keeps the samples
// that did not start, or is removed. A session without samples or group work has no
// entry, so other drafts are never touched. Without a draft or an initialWorkflow
// the workflow starts from the user's last started workflow (model/workflow
// getLastWorkflow). A kit or cohort in either that no longer exists is dropped once
// the lists load, and Start waits for that.
// onStarted(results) is called once starts have run, with startAnalyses' array of
// { sampleKey, status, error?, ... }. The array also carries `kind` ('single' |
// 'batch'), `batch` ({ id, name } or null), `workflow` and `workflowSummary`.
// For one sample the panel confirms the start with a snackbar and closes itself
// (onClose); a batch stays open on its result until the user leaves.
// Group starts call onStarted too, with kind 'multisample' | 'cohort' | 'carrier'.
// onOpenOtherTypes (optional): opens the caller's "Other analysis types" dialog;
// without it the panel opens its own. Both only with FEATURES.otherAnalysisTypesLink:
// every analysis type is in the panel, and the old wizard stays behind the flag.
// ----------------------------------------------------------------------

const SOFT_BUTTON_SX = { height: 36, px: 2, fontSize: 14 }

// The draft saves itself this long after the last change.
const AUTOSAVE_MS = 800
// Review and Start share one button slot: clicks on Start this soon after Review
// opened are ignored, so a double-click on Review never starts a paid run.
const START_GUARD_MS = 700

// A workflow handed in by the caller (a suggestion), completed and made consistent,
// or null.
const presetWorkflowOf = (initialWorkflow) =>
  initialWorkflow && typeof initialWorkflow === 'object' && !Array.isArray(initialWorkflow) && initialWorkflow.analysis
    ? normaliseWorkflow({ ...BASE_WORKFLOW, ...initialWorkflow })
    : null

// The workflow the panel opens with when it does not continue a draft: the caller's
// preset, else the user's last one, else the defaults. `check` marks a preset or
// remembered workflow, whose kit and cohort are checked against the server's lists
// once loaded.
const openingWorkflow = (userKey, initialWorkflow) => {
  const preset = presetWorkflowOf(initialWorkflow)
  if (preset) return { workflow: preset, remembered: false, check: true }
  const remembered = loadLastWorkflow(userKey)
  return {
    workflow: remembered || normaliseWorkflow({ ...BASE_WORKFLOW }),
    remembered: Boolean(remembered),
    check: Boolean(remembered),
  }
}

// The same draft content, for skipping saves that change nothing.
const draftSignature = (workflow, samples, name, groupState) => JSON.stringify({ workflow, samples, name, groupState })

// True while the workflow is still the remembered last one (a refreshed kit name
// aside), for the column's "Same as your last analysis." line.
const sameAsRemembered = (workflow, remembered) =>
  REMEMBERED_FIELDS.every((field) => field === 'kitName' || workflow[field] === remembered[field])

const SOMATIC_INPUTS = ['fastq', 'vcf']

/**
 * The workflow change the first files added to an empty batch make, or null. Their
 * majority decides the input, even while other files are set aside. Somatic takes
 * FASTQ and SNV/Indel VCFs: when none of the added files fits it (all long reads,
 * or CNV and SV VCFs), the analysis turns to Germline, as the files can only run
 * there. Files that do not fit the result are set aside.
 */
export const firstAddPatch = (workflow, added) => {
  if (!isFileAnalysis(workflow) || !Array.isArray(added) || !added.length) return null
  const { input } = majorityInput(added)
  if (!input) return null
  let patch = { input }
  if (workflow.analysis === 'somatic') {
    const somatic = majorityInput(added.filter((s) => SOMATIC_INPUTS.includes(inputOfSample(s)))).input
    patch = somatic ? { input: somatic } : {}
    if (!partitionSamples(added, { ...workflow, ...patch }).inBatch.length) patch = { analysis: 'germline', input }
  }
  return Object.keys(patch).some((field) => workflow[field] !== patch[field]) ? patch : null
}

// Footer copy for the first workflow choice still missing.
const WORKFLOW_NEXT_STEP = {
  analysis: 'Choose an analysis to start',
  input: 'Choose an input to start',
  mode: 'Choose tumour-only or tumour-normal to start',
  seq: 'Choose a sequencing type to start',
  kit: 'Choose a capture kit to start',
  cnvMethod: 'Choose a CNV method to start',
  cohort: 'Choose an ExomeDepth cohort to start',
  reference: 'Choose a reference genome to start',
}

const withoutStop = (text) => String(text || '').replace(/\.$/, '')

export const workflowNextStep = (errors) => {
  const field = Object.keys(errors)[0]
  if (!field) return ''
  return WORKFLOW_NEXT_STEP[field] || withoutStop(errors[field])
}

// Footer copy for the first issue that keeps one sample from starting.
export const sampleNextStep = (sample, issues) => {
  const issue = (issues || []).find((i) => i.blocking)
  if (!issue) return ''
  if (issue.code === 'normal') return 'Choose a normal sample to start'
  if (issue.code === 'ai') {
    const noHpo = !(sample.hpo || []).length
    const noHistory = !String(sample.clinicalHistory || '').trim()
    const missing = noHpo && noHistory ? 'HPO terms and clinical history' : noHpo ? 'HPO terms' : 'clinical history'
    return `Add ${missing}, or run without AI`
  }
  return withoutStop(issue.text)
}

// Cmd/Ctrl+Enter opens the review of a batch, and nothing else: it never starts an
// analysis, and it is ignored in multiline fields and in dialogs over the panel
// (their key events bubble through React to the panel).
export const reviewShortcutApplies = (e, { view, primary, panel }) => {
  if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return false
  if (view !== 'compose' || !primary || !primary.shortcut || primary.disabled) return false
  const target = e.target
  if (target && target.tagName === 'TEXTAREA') return false
  if (panel && target && !panel.contains(target)) return false
  return true
}

// A held Enter repeats clicks on the focused button, and Review and Start share
// one slot: only a fresh key press counts.
export const ignoreHeldEnter = (e) => {
  if (e.repeat && e.key === 'Enter') e.preventDefault()
}

// Moves focus to an element that may render a moment later (a row opening).
const focusWhenReady = (id, tries = 20) => {
  const el = document.getElementById(id)
  if (el) {
    el.focus()
    return
  }
  if (tries > 0) window.setTimeout(() => focusWhenReady(id, tries - 1), 50)
}

// Sample fields and the sheet field whose flag an edit clears. HPO flags are
// dismissed by hand: the HPO picker reports its value on mount.
const SHEET_FIELD_OF = {
  sex: 'sex',
  age: 'age',
  onsetAge: 'onsetAge',
  tumourType: 'tumourType',
  normalFileId: 'normal',
}

const pluralSamples = (n) => `${n} ${n === 1 ? 'sample' : 'samples'}`

const NO_SAMPLES = []

const reasonOf = (issues) => issues.find((i) => i.blocking)?.text || 'Not ready'

const NewAnalysisPanel = ({
  onClose,
  onStarted,
  initialFiles,
  initialSamples,
  initialWorkflow,
  initialName,
  onOpenOtherTypes,
  guardRef,
  continueDraftId,
  onContinueDraft,
  onReset,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { user } = useAuth() || {}
  const userKey = userKeyOf(user)

  // ------------------------------------------------------------ data
  const filesApi = useFiles()
  const fileRows = useMemo(() => (Array.isArray(filesApi.query.data) ? filesApi.query.data : []), [filesApi.query.data])
  const fileMap = useMemo(() => new Map(fileRows.map((row) => [fileIdOf(row), row])), [fileRows])
  const candidates = useMemo(() => groupRows(fileRows), [fileRows])
  const bedQuery = useBedFiles().query
  const bedLoaded = Array.isArray(bedQuery.data)
  const bedFiles = useMemo(() => (Array.isArray(bedQuery.data) ? bedQuery.data : []), [bedQuery.data])
  // Targeted RNA capture panels (R4.9): offered, required and sent only where the
  // backend says it accepts them (GET rna-vocabulary).
  const rnaVocabulary = useRnaVocabulary()?.data
  const rnaCapture = useMemo(() => rnaCaptureOf(rnaVocabulary), [rnaVocabulary])
  // Prices and balance from /organisation/details (model/pricing), null until loaded.
  const prices = usePrices()
  const analysisQuery = useQuery(RECENT_ANALYSES_KEY, fetchRecentAnalyses, RECENT_ANALYSES_OPTIONS)
  const analysed = useMemo(() => analysedFileIds(analysisQuery.data?.results || []), [analysisQuery.data])
  const balance = balanceOf(prices)

  // ------------------------------------------------------------ state
  // A session continues the draft it was reopened on (Continue in the Drafts list), or
  // starts fresh with a new draft id. The caller's files, samples and preset apply to
  // a fresh session only.
  const [opening] = useState(() => {
    const draft = loadDraft(continueDraftId, userKey)
    if (draftHasWork(draft)) {
      return {
        draftId: draft.id,
        workflow: normaliseWorkflow({ ...BASE_WORKFLOW, ...(draft.workflow || {}) }),
        remembered: false,
        draft,
      }
    }
    const next = { ...openingWorkflow(userKey, initialWorkflow), draftId: newDraftId() }
    // Kits already loaded (usually cached) are checked right away.
    return next.check && bedLoaded
      ? { ...next, workflow: reconcileKit(next.workflow, bedFiles), kitChecked: true }
      : next
  })
  // The one draft entry this session writes (model/draft).
  const { draftId } = opening
  const fresh = !opening.draft
  const [workflow, setWorkflow] = useState(opening.workflow)
  // Each analysis and input keeps its own options (model/workflow switchWorkflow).
  // Handlers read and write the workflow through these refs, so two changes in one
  // event build on each other and switchWorkflow never runs in a state updater.
  const workflowRef = useRef(opening.workflow)
  workflowRef.current = workflow
  const memoryRef = useRef({})
  // A preset or remembered kit and cohort are checked once their lists load.
  const [pendingCheck, setPendingCheck] = useState({
    kit: Boolean(opening.check) && !opening.kitChecked,
    cohort: Boolean(opening.check),
  })
  // Every picked sample, those set aside for another batch included.
  const [samples, setSamples] = useState(() => (opening.draft ? opening.draft.samples : []))
  const samplesRef = useRef(samples)
  samplesRef.current = samples
  // A preset name is the field's value, as if typed: it stays until the user changes it.
  const [name, setName] = useState(() => {
    if (opening.draft) return opening.draft.name || ''
    return typeof initialName === 'string' ? initialName.slice(0, 120) : ''
  })
  const nameRef = useRef(name)
  nameRef.current = name
  // The group flows' state (groups/model.js), kept here only for the draft.
  const [groupState, setGroupState] = useState(() => opening.draft?.groupState || EMPTY_GROUP_STATE)
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )
  const [view, setView] = useState('compose') // compose | review | start (file flows)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetNote, setSheetNote] = useState(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [otherOpen, setOtherOpen] = useState(false)
  const [expandedUid, setExpandedUid] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  // A blocked Start or Review click: 'workflow' (a workflow choice is missing) or
  // 'noneReady' (no sample can start). The footer then says what to do in full colour.
  const [footerNote, setFooterNote] = useState(null)
  // Ticked rows of the empty list (EmptySamples), in the list's order; the footer adds them.
  const [selection, setSelection] = useState(NO_SAMPLES)
  // When the footer's Add last ran: with one sample, Start takes its slot right after.
  const addedAtRef = useRef(0)
  // Bumped when the Drafts list changes (a delete, an undo), to read it again.
  const [draftsVersion, setDraftsVersion] = useState(0)
  // error: why a single start did not go; unknown: it got no reply and may have started.
  const [single, setSingle] = useState({ busy: false, error: '', unknown: false })
  const [run, setRun] = useState(null)
  // After a batch start: how many samples of the batch that did not start were kept.
  const [keptCount, setKeptCount] = useState(0)
  const [confirmClose, setConfirmClose] = useState(false)
  const abortRef = useRef(null)
  const startedRef = useRef(false)
  const panelRef = useRef(null)
  const reviewEnteredAtRef = useRef(0)
  const runStartedAtRef = useRef(0)
  // Each sample's last start status across runs (uid -> 'started' | 'failed' | ...).
  const outcomesRef = useRef({})
  const lastSavedRef = useRef(
    opening.draft
      ? draftSignature(
          opening.workflow,
          opening.draft.samples,
          opening.draft.name || '',
          opening.draft.groupState || EMPTY_GROUP_STATE,
        )
      : null,
  )
  const hadWorkRef = useRef(false)

  const commitWorkflow = useCallback((next) => {
    workflowRef.current = next
    setWorkflow(next)
  }, [])

  // Every workflow change goes through switchWorkflow, in the event handler.
  const applyWorkflow = useCallback(
    (patch) => {
      const { workflow: next, memory } = switchWorkflow(workflowRef.current, patch, memoryRef.current)
      memoryRef.current = memory
      commitWorkflow(next)
    },
    [commitWorkflow],
  )

  // The column's changes. Another analysis starts without the red reasons of a
  // blocked click on the one before.
  const changeWorkflow = useCallback(
    (patch) => {
      if (patch.analysis && patch.analysis !== workflowRef.current.analysis) {
        setShowErrors(false)
        setFooterNote(null)
      }
      applyWorkflow(patch)
    },
    [applyWorkflow],
  )

  // ------------------------------------------------------------ derived
  const groupMode = isGroupAnalysis(workflow)
  const tn = isTumourNormal(workflow)
  // The batch is the samples that fit the analysis and input; the rest are set aside.
  const partition = useMemo(
    () => partitionSamples(samples, workflow),
    // Only the analysis and input decide what fits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, workflow.analysis, workflow.input],
  )
  const inBatch = partition.inBatch || NO_SAMPLES
  const setAside = partition.setAside || NO_SAMPLES
  const normalsInUse = useMemo(() => (tn ? normalFileIdsInUse(inBatch) : new Set()), [tn, inBatch])
  const rows = useMemo(() => {
    const batch = tn ? inBatch.filter((s) => !normalsInUse.has(s.fileId)) : inBatch
    // One sample runs with the column's options: its own options apply in a table.
    if (batch.length === 1 && batch[0].longRead) return [{ ...batch[0], longRead: null }]
    return batch
  }, [tn, inBatch, normalsInUse])
  const duplicateKeys = useMemo(() => duplicateKeysOf(rows), [rows])
  const issuesByUid = useMemo(() => {
    const map = new Map()
    rows.forEach((s) => map.set(s.uid, computeIssues(s, { workflow, fileMap, duplicateKeys, rnaCapture })))
    return map
  }, [rows, workflow, fileMap, duplicateKeys, rnaCapture])
  const startable = useMemo(
    () => rows.filter((s) => !(issuesByUid.get(s.uid) || []).some((i) => i.blocking)),
    [rows, issuesByUid],
  )
  const excluded = useMemo(
    () =>
      rows
        .filter((s) => (issuesByUid.get(s.uid) || []).some((i) => i.blocking))
        .map((s) => ({ sample: s, reason: reasonOf(issuesByUid.get(s.uid)) })),
    [rows, issuesByUid],
  )
  const errors = useMemo(() => validateWorkflow(workflow), [workflow])
  const workflowValid = Object.keys(errors).length === 0
  // The estimate for the samples that will start, each at its own price (AI per
  // sample, CNV per sample). Worked out before the prices load too, for the AI count.
  const price = useMemo(
    () => (!groupMode && workflowValid && startable.length ? batchPrice(workflow, startable, prices) : null),
    [groupMode, workflowValid, workflow, startable, prices],
  )
  // The workflow column's line: the batch's price per sample and what AI adds.
  const unitPrice = useMemo(
    () => (!groupMode && prices ? workflowUnitPrice(workflow, prices) : null),
    [groupMode, workflow, prices],
  )
  const batchName = name.trim() || autoBatchName(workflow)
  // Every file already picked, in this batch or set aside.
  const pickedIds = useMemo(() => new Set(samples.flatMap((s) => s.fileIds || [])), [samples])
  // What the footer asks for next: the first missing workflow choice, else (one
  // sample) the first issue that keeps it from starting.
  const nextStep =
    workflowNextStep(errors) || (rows.length === 1 ? sampleNextStep(rows[0], issuesByUid.get(rows[0].uid)) : '')
  const rememberedHint = opening.remembered && sameAsRemembered(workflow, opening.workflow)
  const hasWork = samples.length > 0 || groupStateHasWork(groupState)

  // Uploads waiting for an analysis, per input of the current analysis (the column's
  // hint and the empty list's "Also waiting").
  const awaiting = useMemo(() => recentUploads(candidates, analysed, { limit: Infinity }), [candidates, analysed])
  const waitingByInput = useMemo(() => {
    const out = {}
    if (!isFileAnalysis(workflow.analysis)) return out
    ;(INPUT_OPTIONS[workflow.analysis] || []).forEach(({ value }) => {
      const scope = { analysis: workflow.analysis, input: value }
      out[value] = awaiting.filter((c) => candidateFitsWorkflow(c, scope)).length
    })
    return out
  }, [awaiting, workflow.analysis])
  const longReadStats = useMemo(
    () => ({ total: rows.length, methylationReady: rows.filter((s) => s.methylationReady).length }),
    [rows],
  )

  // Normals offered for a tumour: paired, verified FASTQ samples, a normal's lanes
  // as one.
  const normalCandidates = useMemo(
    () =>
      candidates.filter(
        (c) => c.fileType === 'fastq' && c.rows.every((row) => !isSingleReadRow(row) && fileStateOf(row) === 'ready'),
      ),
    [candidates],
  )
  const normalOptionsByUid = useMemo(() => {
    const map = new Map()
    if (!tn) return map
    rows.forEach((s) => {
      const suggestion = suggestNormal(s, normalCandidates)
      const options = normalCandidates
        .filter((c) => c.id !== s.fileId)
        .map((c) => ({
          id: c.id,
          key: c.key,
          fileIds: c.rows.map(fileIdOf),
          suggested: suggestion ? suggestion.id === c.id : false,
        }))
      options.sort((a, b) => Number(b.suggested) - Number(a.suggested))
      map.set(s.uid, options)
    })
    return map
  }, [tn, rows, normalCandidates])
  const normalOptionsFor = useCallback((s) => normalOptionsByUid.get(s.uid) || [], [normalOptionsByUid])

  // Tumour-normal: suggest pairs from names (_T/_N, -tumour/-normal, -FFPE/-blood).
  // A suggestion stays marked until the user picks a normal.
  useEffect(() => {
    if (!tn || !normalCandidates.length) return
    const batchUids = new Set(inBatch.map((s) => s.uid))
    let changed = false
    const next = samples.map((s) => {
      if (!batchUids.has(s.uid) || s.normalFileId || s.normalTouched || normalsInUse.has(s.fileId)) return s
      const suggestion = suggestNormal(s, normalCandidates)
      if (!suggestion) return s
      changed = true
      return { ...s, ...normalFieldsOf(suggestion), normalSuggested: true }
    })
    if (changed) setSamples(next)
  }, [tn, samples, inBatch, normalCandidates, normalsInUse])

  // ------------------------------------------------------------ handlers
  const addSamples = useCallback(
    (cands) => {
      if (!cands.length) return
      const current = samplesRef.current
      const { samples: next } = addCandidates(current, cands)
      // The first files of an empty batch decide its input; set-aside samples do not
      // count, and files of another type are set aside.
      const wf = workflowRef.current
      if (isFileAnalysis(wf) && !partitionSamples(current, wf).inBatch.length) {
        const before = new Set(current.map((s) => s.uid))
        const patch = firstAddPatch(
          wf,
          next.filter((s) => !before.has(s.uid)),
        )
        if (patch) applyWorkflow(patch)
      }
      samplesRef.current = next
      setSamples(next)
      // Whatever brought them in, the empty list's ticks are done with.
      setSelection(NO_SAMPLES)
    },
    [applyWorkflow],
  )

  // Another analysis or input lists other uploads: the ticks go.
  useEffect(() => {
    setSelection(NO_SAMPLES)
  }, [workflow.analysis, workflow.input])

  // The workflow a pick would run with: the first files of an empty batch decide it.
  const resolvePickWorkflow = useCallback(
    (cands) => {
      const wf = workflowRef.current
      if (!isFileAnalysis(wf) || partitionSamples(samplesRef.current, wf).inBatch.length) return wf
      const patch = firstAddPatch(wf, addCandidates([], cands).samples)
      return patch ? { ...wf, ...patch } : wf
    },
    // Reads the refs, which are current at every call.
    [],
  )

  // Makes a set-aside group the batch; the batch's own samples are set aside in turn.
  const startSetAside = useCallback(
    (group) => {
      applyWorkflow({ analysis: group.analysis, input: group.input })
      setName('')
      setExpandedUid(null)
      setSheetOpen(false)
      setSheetNote(null)
      setFooterNote(null)
      setShowErrors(false)
      setSingle({ busy: false, error: '', unknown: false })
    },
    [applyWorkflow],
  )

  // Initial files, once the file list has loaded (not in a continued draft).
  const initialDone = useRef(!fresh)
  useEffect(() => {
    if (initialDone.current || !Array.isArray(initialFiles) || !initialFiles.length || !fileRows.length) return
    initialDone.current = true
    const ids = new Set(initialFiles.map((f) => String(typeof f === 'object' ? fileIdOf(f) : f)))
    const picked = fileRows.filter((row) => ids.has(String(fileIdOf(row))))
    if (picked.length) addSamples(groupRows(picked))
  }, [initialFiles, fileRows, addSamples])

  // Initial samples, added once on open like a pick from the list (not in a continued
  // draft).
  const initialSamplesDone = useRef(!fresh)
  useEffect(() => {
    if (initialSamplesDone.current || !Array.isArray(initialSamples) || !initialSamples.length) return
    initialSamplesDone.current = true
    addSamples(initialSamples.filter((c) => c && Array.isArray(c.rows) && c.rows.length))
  }, [initialSamples, addSamples])

  // A preset or remembered kit that is gone or no longer verified is dropped once the
  // kits load.
  useEffect(() => {
    if (!pendingCheck.kit || !bedLoaded) return
    setPendingCheck((prev) => ({ ...prev, kit: false }))
    commitWorkflow(reconcileKit(workflowRef.current, bedFiles))
  }, [pendingCheck.kit, bedLoaded, bedFiles, commitWorkflow])

  // The same for a preset or remembered CNV cohort, with the cohort list the CNV
  // options read.
  const cohortCheck = pendingCheck.cohort && namesCohort(workflow)
  const cohortQuery = useQuery(cnvCohortsQueryKey(workflow.cnvMethod), () => fetchCnvCohorts(workflow.cnvMethod), {
    enabled: cohortCheck,
    staleTime: 60 * 1000,
  })
  useEffect(() => {
    if (!pendingCheck.cohort) return
    if (!cohortCheck) {
      setPendingCheck((prev) => ({ ...prev, cohort: false }))
      return
    }
    if (!cohortQuery.data) return
    const method = workflow.cnvMethod
    setPendingCheck((prev) => ({ ...prev, cohort: false }))
    commitWorkflow(reconcileCohort(workflowRef.current, method, cohortQuery.data))
  }, [pendingCheck.cohort, cohortCheck, cohortQuery.data, workflow.cnvMethod, commitWorkflow])

  // Start and Review wait for those checks (a moment, until the lists load), so a
  // kit or cohort that is gone is never sent. A list that fails to load does not
  // hold them up; the server still checks the value.
  const checkingRemembered = (pendingCheck.kit && !bedQuery.isError) || (cohortCheck && !cohortQuery.isError)

  // Editing a value clears the sheet's flag on that field.
  const changeSample = useCallback((uid, patch) => {
    setSamples((prev) =>
      prev.map((s) => {
        if (s.uid !== uid) return s
        const next = { ...s, ...patch }
        if (Object.prototype.hasOwnProperty.call(patch, 'normalFileId')) next.normalTouched = true
        const cleared = Object.keys(patch)
          .map((k) => SHEET_FIELD_OF[k])
          .filter((f) => f && s.sheetErrors && s.sheetErrors[f])
        if (cleared.length) {
          next.sheetErrors = { ...s.sheetErrors }
          cleared.forEach((f) => delete next.sheetErrors[f])
        }
        return next
      }),
    )
  }, [])

  // A removal offers Undo, which puts the samples back in their places, with their
  // details, while the panel is open and nothing has started.
  const removeToastsRef = useRef([])
  useEffect(() => () => removeToastsRef.current.forEach((key) => closeSnackbar(key)), [closeSnackbar])
  const removeSamples = useCallback(
    (uids) => {
      const drop = new Set(uids)
      const removed = samplesRef.current.map((s, index) => ({ s, index })).filter(({ s }) => drop.has(s.uid))
      setSamples((prev) => prev.filter((s) => !drop.has(s.uid)))
      setExpandedUid((current) => (drop.has(current) ? null : current))
      if (!removed.length) return
      const undo = () => {
        if (!mountedRef.current || startedRef.current) return
        setSamples((prev) => {
          const present = new Set(prev.map((s) => s.uid))
          const next = [...prev]
          removed.forEach(({ s, index }) => {
            if (!present.has(s.uid)) next.splice(Math.min(index, next.length), 0, s)
          })
          return next
        })
      }
      const toastKey = enqueueSnackbar(
        removed.length === 1 ? `Removed ${removed[0].s.key}` : `Removed ${removed.length} samples`,
        {
          variant: 'info',
          autoHideDuration: 6000,
          action: (key) => (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                closeSnackbar(key)
                undo()
              }}
              sx={{ fontWeight: 600, minWidth: 0 }}
            >
              Undo
            </Button>
          ),
        },
      )
      removeToastsRef.current.push(toastKey)
    },
    [enqueueSnackbar, closeSnackbar],
  )

  // Demultiplexed lanes of one sample uploaded under different names become one.
  const mergeRows = useCallback((uids) => {
    setSamples((prev) => mergeSamples(prev, uids))
    setExpandedUid(null)
  }, [])

  const bulk = useCallback((uids, action) => {
    const set = new Set(uids)
    setSamples((prev) =>
      prev.map((s) => {
        if (!set.has(s.uid)) return s
        const next = { ...s }
        if (action.patient && canSavePatient(s)) {
          Object.assign(next, action.patient)
          next.changed = { ...(s.changed || {}) }
          Object.keys(action.patient).forEach((field) => {
            next.changed[field] = true
          })
        }
        if (action.addHpo) next.hpo = mergeHpo(s.hpo, action.addHpo)
        if (Object.prototype.hasOwnProperty.call(action, 'tumourType')) next.tumourType = action.tumourType
        const cleared = [
          ...Object.keys(action.patient || {}),
          ...(action.tumourType !== undefined ? ['tumourType'] : []),
        ]
          .map((k) => SHEET_FIELD_OF[k])
          .filter((f) => f && s.sheetErrors && s.sheetErrors[f])
        if (cleared.length) {
          next.sheetErrors = { ...s.sheetErrors }
          cleared.forEach((f) => delete next.sheetErrors[f])
        }
        return next
      }),
    )
  }, [])

  const fix = useCallback(
    (uid, fixName, issue) => {
      if (fixName === 'remove') removeSamples([uid])
      else if (fixName === 'aiOff' || fixName === 'noAI') changeSample(uid, aiPatch(workflowRef.current, false))
      else if (fixName === 'pickNormal') setExpandedUid(uid)
      else if (fixName === 'addPhenotype') {
        // The row opens (one sample: the editor is open already) on its HPO search.
        setExpandedUid(uid)
        focusWhenReady(`hpo-input-${uid}`)
      } else if (fixName === 'confirmNormal') changeSample(uid, { normalSuggested: false })
      else if (fixName === 'split') setSamples((prev) => splitLanes(prev, uid, fileMap))
      else if (fixName === 'dismissSheet' && issue?.field) {
        setSamples((prev) =>
          prev.map((s) => {
            if (s.uid !== uid) return s
            const sheetErrors = { ...(s.sheetErrors || {}) }
            delete sheetErrors[issue.field]
            return { ...s, sheetErrors }
          }),
        )
      }
    },
    [removeSamples, changeSample, fileMap],
  )

  // A sheet's values go to the samples its rows matched. Rows that matched an upload
  // not in the batch bring it in first (additions), with their values.
  const applySheet = (updates, summary, additions = []) => {
    if (additions.length) addSamples(additions.map((a) => a.candidate))
    setSamples((prev) =>
      withSheetAdditions(
        prev.map((s) => (updates.has(s.uid) ? { ...s, ...updates.get(s.uid) } : s)),
        additions,
      ),
    )
    setSheetOpen(false)
    const added = summary.added ? ` ${pluralSamples(summary.added)} joined the batch.` : ''
    setSheetNote(
      `${summary.name} applied: ${summary.columns} ${summary.columns === 1 ? 'column' : 'columns'}, ${
        summary.matched
      } of ${summary.rows} rows.${added}${summary.flagged ? ` ${pluralSamples(summary.flagged)} need attention.` : ''}`,
    )
  }

  const refreshAfterStart = useCallback(() => {
    queryClient.invalidateQueries('all-analysis')
    queryClient.invalidateQueries(BATCHES_QUERY_KEY)
    queryClient.invalidateQueries(ANALYSIS_STATS_KEY)
    queryClient.invalidateQueries('organisation-details')
  }, [queryClient])

  // ------------------------------------------------------------ own draft
  // This session's entry in the Drafts list, the only one it writes or removes.
  const saveOwnDraft = useCallback((draft) => saveDraft({ ...draft, id: draftId }, userKey), [draftId, userKey])
  const removeOwnDraft = useCallback(() => deleteDraft(draftId, userKey), [draftId, userKey])

  // ------------------------------------------------------------ group flows
  // Multisample, CNV cohort and Carrier: the hook is always called, and inert while
  // a file analysis is chosen. Its state comes back through onStateChange for the draft.
  const onGroupStarted = useCallback(
    (results) => {
      if (onStarted) onStarted(results)
    },
    [onStarted],
  )
  // The flow's Close (after a group run) keeps what is left as the draft right away:
  // the autosave waits a moment, and the groups that started must not come back.
  const closeFromGroupFlow = () => {
    if (!startedRef.current) {
      if (hasWork) saveOwnDraft({ workflow, samples, name, groupState })
      else if (hadWorkRef.current) removeOwnDraft()
    }
    onClose()
  }
  const showColumnErrors = useCallback(() => setShowErrors(true), [])
  // A group run closed with "Close anyway" goes on until the request being sent is
  // answered; its pruned state then arrives after the panel is gone and is saved as
  // the draft here, so what started does not come back on the next open.
  const changeGroupState = useCallback(
    (next) => {
      if (mountedRef.current) {
        setGroupState(next)
        return
      }
      const rest = samplesRef.current
      if (rest.length || groupStateHasWork(next)) {
        saveOwnDraft({ workflow: workflowRef.current, samples: rest, name: nameRef.current, groupState: next })
      } else removeOwnDraft()
    },
    [saveOwnDraft, removeOwnDraft],
  )
  const flow = useGroupFlow({
    active: groupMode,
    workflow,
    bedFiles,
    candidates,
    fileMap,
    org: prices,
    initialState: opening.draft?.groupState || null,
    onStateChange: changeGroupState,
    onStarted: onGroupStarted,
    onClose: closeFromGroupFlow,
    showErrors,
    // Carrier batches take the header's name.
    batchName: name,
    onShowErrors: showColumnErrors,
  })
  const activeView = groupMode ? flow.view : view

  // ------------------------------------------------------------ starting
  const notifyStarted = (kind, batch, results) => {
    if (!onStarted) return
    const payload = [...results]
    payload.kind = kind
    payload.batch = batch
    payload.workflow = workflow
    payload.workflowSummary = describeWorkflow(workflow)
    onStarted(payload)
  }

  // After a start, the session's draft keeps what is left: samples that did not start
  // (and set-aside ones, which never went) and the group work. With nothing left it
  // goes.
  const keepRestAsDraft = (kept) => {
    if (kept.length || groupStateHasWork(groupState)) saveOwnDraft({ workflow, samples: kept, name, groupState })
    else removeOwnDraft()
  }

  const startSingle = async () => {
    const sample = rows[0]
    if (!sample) return
    setSingle({ busy: true, error: '', unknown: false })
    const results = await startAnalyses({ workflow, samples: [withParsedAges(sample)], rnaCapture })
    const result = results[0] || {}
    const rest = () => samplesRef.current.filter((s) => s.uid !== sample.uid)
    if (result.status === 'started') {
      startedRef.current = true
      saveLastWorkflow(workflow, userKey)
      keepRestAsDraft(rest())
      refreshAfterStart()
      notifyStarted('single', null, results)
      // The panel confirms the start itself; Home only refreshes its lists.
      // TODO(batches): highlight the new History row once the start response
      // returns the analysis id (backend Phase 1b).
      enqueueSnackbar(`Analysis started for ${sample.key}`, { variant: 'success' })
      if (result.warning) enqueueSnackbar(`${result.warning} for ${sample.key}`, { variant: 'warning' })
      onClose()
      return
    }
    if (result.status === 'unknown') {
      // It may have started: the lists refresh so History can show it. Treat the
      // session as started so autosave stops and the draft does not bring the
      // sample back, and keep Start disabled so a second click cannot run (and
      // charge) the same analysis twice. The user checks History instead.
      startedRef.current = true
      keepRestAsDraft(rest())
      refreshAfterStart()
      setSingle({ busy: false, error: result.error || START_ERRORS.no_reply, unknown: true })
      return
    }
    if (result.code === 'refused') {
      // Nothing was written or charged: the reason reads as a plain note.
      setSingle({ busy: false, error: result.error, unknown: false, refused: true })
      return
    }
    let error = result.error || 'Could not start the analysis'
    if (result.code === 'insufficient_balance') {
      error = result.neededBalance
        ? `Not enough balance. Add ${formatCredits(Number(result.neededBalance))} credits and try again.`
        : 'Not enough balance. Top up and try again.'
    }
    setSingle({ busy: false, error, unknown: false })
  }

  const runBatch = async (toStart, batch) => {
    const controller = new AbortController()
    abortRef.current = controller
    runStartedAtRef.current = Date.now()
    const batchUids = new Set(rows.map((s) => s.uid))
    setRun((prev) => {
      const order = prev?.order || toStart.map((s) => s.uid)
      const keys = { ...(prev?.keys || {}) }
      const status = { ...(prev?.status || {}) }
      toStart.forEach((s) => {
        keys[s.uid] = s.key
        status[s.uid] = { status: 'queued' }
      })
      return { batch, order, keys, status, samples: prev?.samples || toStart, running: true }
    })
    setView('start')
    const results = await startAnalyses(
      { workflow, samples: toStart.map(withParsedAges), batch, rnaCapture },
      {
        signal: controller.signal,
        onProgress: (index, status, detail) => {
          const uid = toStart[index]?.uid
          if (!uid) return
          setRun((prev) => (prev ? { ...prev, status: { ...prev.status, [uid]: { status, detail } } } : prev))
        },
      },
    )
    abortRef.current = null
    setRun((prev) => (prev ? { ...prev, running: false } : prev))
    results.forEach((r, i) => {
      if (toStart[i]) outcomesRef.current[toStart[i].uid] = r.status
    })
    const startedCount = results.filter((r) => r.status === 'started').length
    const unknownCount = results.filter((r) => r.status === 'unknown').length
    if (startedCount) {
      startedRef.current = true
      saveLastWorkflow(workflow, userKey)
    }
    if (startedCount || unknownCount) refreshAfterStart()
    // The draft keeps what did not start (failed, skipped or never ready, and the
    // set-aside samples), not what started or may have started.
    const kept = notStartedSamples(samples, (uid) => outcomesRef.current[uid], { tumourNormal: tn })
    keepRestAsDraft(kept)
    setKeptCount(kept.filter((s) => batchUids.has(s.uid)).length)
    if (startedCount) notifyStarted('batch', batch, results)
  }

  // After a run: the next set-aside batch, without what started or may have started.
  const continueWith = (group) => {
    const kept = notStartedSamples(samplesRef.current, (uid) => outcomesRef.current[uid], { tumourNormal: tn })
    samplesRef.current = kept
    setSamples(kept)
    outcomesRef.current = {}
    startedRef.current = false
    lastSavedRef.current = null
    setRun(null)
    setKeptCount(0)
    setView('compose')
    startSetAside(group)
  }

  const enterReview = () => {
    reviewEnteredAtRef.current = Date.now()
    setView('review')
  }

  const startReviewed = () => {
    if (!startable.length) return
    // The second click of a double-click on Review lands here.
    if (Date.now() - reviewEnteredAtRef.current < START_GUARD_MS) return
    runBatch(startable, newBatch(batchName))
  }

  const retryFailed = () => {
    if (!run) return
    const failed = run.samples.filter((s) => ['failed', 'skipped'].includes(run.status[s.uid]?.status))
    if (failed.length) runBatch(failed, run.batch)
  }

  // ------------------------------------------------------------ draft
  // The session's draft saves itself while the user composes, and goes when every
  // sample and group is removed. Other drafts are never touched.
  useEffect(() => {
    if (startedRef.current || (view !== 'compose' && view !== 'review')) return undefined
    if (!hasWork) {
      if (hadWorkRef.current) {
        hadWorkRef.current = false
        removeOwnDraft()
        lastSavedRef.current = null
      }
      return undefined
    }
    hadWorkRef.current = true
    const timer = window.setTimeout(() => {
      if (startedRef.current) return
      const signature = draftSignature(workflow, samples, name, groupState)
      if (signature === lastSavedRef.current) return
      if (saveOwnDraft({ workflow, samples, name, groupState })) lastSavedRef.current = signature
    }, AUTOSAVE_MS)
    return () => window.clearTimeout(timer)
  }, [workflow, samples, name, groupState, hasWork, view, saveOwnDraft, removeOwnDraft])

  // A reload or a closed tab: the last edits are saved now, and a start in progress
  // asks first (the samples not sent yet would not start).
  const starting = Boolean(run?.running) || single.busy || flow.running
  const latestDraftRef = useRef(null)
  latestDraftRef.current =
    !starting && hasWork && (view === 'compose' || view === 'review') ? { workflow, samples, name, groupState } : null
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (starting) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
      const draft = latestDraftRef.current
      if (draft && !startedRef.current) saveOwnDraft(draft)
      return undefined
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [starting, saveOwnDraft])

  // The empty samples area (EmptySamples) lists the user's other drafts.
  const emptyList = !groupMode && view === 'compose' && !sheetOpen && rows.length === 0
  const otherDrafts = useMemo(
    () => (emptyList ? loadDrafts(userKey).filter((d) => d.id !== draftId && draftHasWork(d)) : NO_SAMPLES),
    // draftsVersion: read again after a delete or an undo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emptyList, userKey, draftId, draftsVersion],
  )

  // Continue: this session's work, if any, is saved first; the dialog then reopens
  // the panel on that draft, which becomes the session's draft.
  const continueDraft = (draft) => {
    if (hasWork && !startedRef.current) saveOwnDraft({ workflow, samples, name, groupState })
    if (onContinueDraft) onContinueDraft(draft.id)
  }

  // Reset: the dialog opens a fresh panel in this one's place. What was here leaves
  // the Drafts list and comes back with the Undo the dialog offers.
  const composing = groupMode ? flow.view === 'compose' : view === 'compose'
  const canReset = composing && (hasWork || name.trim() !== '')
  const resetPanel = () => {
    const snapshot =
      hasWork && !startedRef.current ? { id: draftId, workflow, samples, name, groupState, savedAt: Date.now() } : null
    removeOwnDraft()
    // The autosave and the close must not bring it back.
    startedRef.current = true
    if (onReset) onReset(snapshot, userKey)
  }

  const deleteOtherDraft = (draft) => {
    const removed = deleteDraft(draft.id, userKey)
    setDraftsVersion((n) => n + 1)
    if (!removed) return
    enqueueSnackbar('Draft deleted', {
      variant: 'info',
      autoHideDuration: 8000,
      action: (key) => (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            closeSnackbar(key)
            // Drafts filled up again meanwhile: the draft stays deleted rather than push
            // another one out.
            if (!restoreDraft(removed, userKey))
              enqueueSnackbar('Could not bring the draft back', { variant: 'warning' })
            if (mountedRef.current) setDraftsVersion((n) => n + 1)
          }}
          sx={{ fontWeight: 600, minWidth: 0 }}
        >
          Undo
        </Button>
      ),
    })
  }

  // ------------------------------------------------------------ closing
  // Closing saves the session's draft whenever samples or groups were added and
  // nothing started.
  const saveAndClose = useCallback(() => {
    if (hasWork && !startedRef.current && saveOwnDraft({ workflow, samples, name, groupState })) {
      enqueueSnackbar('Saved to Drafts', { variant: 'info' })
    }
    onClose()
  }, [hasWork, samples, workflow, name, groupState, saveOwnDraft, onClose, enqueueSnackbar])

  const requestClose = useCallback(() => {
    if (run?.running || flow.running) {
      setConfirmClose(true)
      return
    }
    if (!groupMode && run && !run.running) {
      onClose()
      return
    }
    saveAndClose()
  }, [run, flow.running, groupMode, onClose, saveAndClose])

  useEffect(() => {
    if (guardRef) guardRef.current = requestClose
    return () => {
      if (guardRef) guardRef.current = null
    }
  }, [guardRef, requestClose])

  // ------------------------------------------------------------ footer
  // A blocked click's note clears once its cause is gone.
  useEffect(() => {
    if (workflowValid) setFooterNote((note) => (note === 'workflow' ? null : note))
  }, [workflowValid])
  useEffect(() => {
    if (startable.length) setFooterNote((note) => (note === 'noneReady' ? null : note))
  }, [startable.length])

  const count = rows.length
  // Rows ticked in the empty list: the footer adds them.
  const selecting = emptyList && selection.length > 0
  let primary = null
  let secondary = null
  let summary = ''

  if (groupMode) {
    // The group flow owns its footer. A blocked click also shows the column's
    // missing choices (capture kit, cohort name, carrier panel).
    const groupPrimary = flow.footer?.primary || null
    primary = groupPrimary && {
      ...groupPrimary,
      onClick: () => {
        if (flow.view === 'compose' && !workflowValid) setShowErrors(true)
        groupPrimary.onClick()
      },
    }
    secondary = flow.footer?.secondary || null
    summary = flow.footer?.text || ''
  } else if (view === 'compose') {
    // "12 samples ready · 7 with AI · estimated 119 credits · balance 1,713". A price
    // the server does not expose yet is confirmed at start, never guessed.
    const priceText =
      price && prices ? (price.known ? `estimated ${creditsText(price.total)}` : 'price confirmed at start') : ''
    const balanceText =
      balance === null
        ? ''
        : price?.known && price.short > 0
        ? `balance ${formatCredits(balance)} is not enough`
        : `balance ${formatCredits(balance)}`
    if (count === 0) summary = 'Add samples to continue'
    else if (nextStep) summary = nextStep
    else if (count === 1) {
      // The one sample's own AI choice, where it differs from the batch's.
      const own = { ...workflow, interpretation: effectiveAI(workflow, rows[0]) ? 'ai' : 'standard' }
      summary = [describeWorkflow(own), priceText, balanceText].filter(Boolean).join(' · ')
    } else {
      const ready = startable.length
      const noun = tn ? 'analyses' : 'samples'
      const readyText = ready === count ? `${count} ${noun} ready` : `${ready} of ${count} ready`
      const aiText = price?.withAI ? `${price.withAI} with AI` : ''
      summary = [readyText, aiText, priceText, balanceText].filter(Boolean).join(' · ')
    }

    // While the sheet import is open it has the only buttons.
    if (sheetOpen) {
      primary = null
    } else if (selecting) {
      // In the list's order, so the batch reads as the list did. No shortcut: Cmd or
      // Ctrl+Enter only ever opens a review.
      summary = `${selection.length} selected`
      primary = {
        label: `Add ${pluralSamples(selection.length)}`,
        onClick: () => {
          addedAtRef.current = Date.now()
          addSamples(selection)
        },
      }
    } else if (count <= 1) {
      const blocked = count === 1 && (issuesByUid.get(rows[0].uid) || []).some((i) => i.blocking)
      primary = {
        label: 'Start analysis',
        disabled: count === 0 || blocked || single.busy || single.unknown || checkingRemembered,
        busy: single.busy,
        onClick: () => {
          // The second click of a double-click on Add lands here.
          if (Date.now() - addedAtRef.current < START_GUARD_MS) return
          if (!workflowValid) {
            setShowErrors(true)
            setFooterNote('workflow')
            return
          }
          startSingle()
        },
      }
      if (single.unknown) secondary = { label: 'Check Analysis History', onClick: onClose }
    } else {
      primary = {
        label: `Review ${tn ? `${count} analyses` : pluralSamples(count)}`,
        disabled: checkingRemembered,
        // The only action a keyboard shortcut may take.
        shortcut: true,
        onClick: () => {
          if (!workflowValid) {
            setShowErrors(true)
            setFooterNote('workflow')
            return
          }
          if (!startable.length) {
            setFooterNote('noneReady')
            return
          }
          setFooterNote(null)
          enterReview()
        },
      }
    }
  } else if (view === 'review') {
    secondary = { label: 'Back', onClick: () => setView('compose') }
    primary = {
      label: `Start ${startable.length} ${startable.length === 1 ? 'analysis' : 'analyses'}`,
      disabled: !startable.length,
      onClick: startReviewed,
    }
    summary = price && balance !== null ? `Balance ${formatCredits(balance)}` : ''
  } else if (view === 'start' && run) {
    const failedCount = run.order.filter((uid) => ['failed', 'skipped'].includes(run.status[uid]?.status)).length
    const startedCount = run.order.filter((uid) => run.status[uid]?.status === 'started').length
    const goToBatch = {
      label: 'Go to batch',
      onClick: () => {
        navigate(batchPath(run.batch.id))
        onClose()
      },
    }
    const close = { label: 'Close', onClick: onClose }
    if (run.running) {
      secondary = {
        label: 'Stop',
        onClick: () => {
          // Stop takes the slot of Start and Retry: the second click of a double-click
          // on them lands here and is not a request to stop.
          if (Date.now() - runStartedAtRef.current < START_GUARD_MS) return
          if (abortRef.current) abortRef.current.abort()
        },
      }
      summary = 'Samples not sent yet stop after the current one.'
    } else {
      // Starts with no reply are not retried: they may have started.
      if (failedCount) {
        primary = { label: `Retry ${failedCount}`, onClick: retryFailed }
        secondary = startedCount ? goToBatch : close
      } else if (startedCount) {
        primary = goToBatch
        secondary = close
      } else {
        secondary = close
      }
      if (keptCount) summary = `${keptCount} not started ${keptCount === 1 ? 'is' : 'are'} kept in Drafts.`
    }
  }

  const onKeyDown = (e) => {
    if (!reviewShortcutApplies(e, { view: activeView, primary, panel: panelRef.current })) return
    e.preventDefault()
    primary.onClick()
  }

  let footerText = null
  if (groupMode) {
    footerText = flow.footer?.emphasis ? (
      <Typography sx={{ fontSize: 14 }}>{summary}</Typography>
    ) : (
      <Muted sx={{ fontSize: 14 }}>{summary}</Muted>
    )
  } else if (selecting) {
    footerText = <Muted sx={{ fontSize: 14 }}>{summary}</Muted>
  } else if (single.error && view === 'compose' && count <= 1) {
    footerText = (
      <Typography
        sx={{ fontSize: 14, color: single.refused ? 'text.secondary' : single.unknown ? 'text.primary' : 'error.main' }}
        data-testid={single.refused ? 'single-start-refused' : undefined}
      >
        {single.error}
      </Typography>
    )
  } else if (view === 'compose' && footerNote === 'workflow' && nextStep) {
    footerText = <Typography sx={{ fontSize: 14 }}>{nextStep}</Typography>
  } else if (view === 'compose' && footerNote === 'noneReady' && !startable.length) {
    footerText = <Typography sx={{ fontSize: 14 }}>No sample is ready yet.</Typography>
  } else {
    footerText = <Muted sx={{ fontSize: 14 }}>{summary}</Muted>
  }

  // The workflow column's price line: "7 credits per sample, AI adds 7". Each
  // sample's own price shows in the table, the review and the one-sample editor.
  let columnPriceText = ''
  if (unitPrice && count && workflowValid) {
    if (unitPrice.credits === null) columnPriceText = 'Price confirmed at start'
    else {
      const aiText =
        unitPrice.ai !== null && aiApplies(workflow, rows) ? `, AI adds ${formatCredits(unitPrice.ai)}` : ''
      columnPriceText = `${creditsText(unitPrice.credits)} per sample${aiText}`
    }
  }

  // ------------------------------------------------------------ render
  const headerCount = groupMode
    ? flow.headerCount || ''
    : count
    ? tn && normalsInUse.size
      ? `${count} ${count === 1 ? 'analysis' : 'analyses'} · ${pluralSamples(inBatch.length)}`
      : pluralSamples(count)
    : ''
  const headerTitle = groupMode ? flow.headerTitle || '' : ''
  // A batch (2+ samples, or a group flow) shows its default name as the header's
  // text; one sample runs on its own and the header reads "New analysis".
  const headerName = useNameWithDefault(name, groupMode || count >= 2 ? autoBatchName(workflow) : '', setName)

  // After a run, the set-aside batches are offered next.
  const waiting =
    !groupMode && view === 'start' && run && !run.running
      ? setAside.map((group) => ({
          label: `Start a batch with the ${setAsideFiles(group)}`,
          onStart: () => continueWith(group),
        }))
      : []

  let samplesArea
  if (groupMode) {
    samplesArea = flow.samplesArea
  } else if (sheetOpen) {
    samplesArea = (
      <SheetImport
        samples={rows}
        normalCandidates={normalCandidates}
        workflow={workflow}
        candidates={candidates}
        analysed={analysed}
        onApply={applySheet}
        onClose={() => setSheetOpen(false)}
      />
    )
  } else if (count === 0) {
    samplesArea = (
      <EmptySamples
        candidates={candidates}
        analysed={analysed}
        isLoading={filesApi.query.isLoading}
        workflow={workflow}
        waitingByInput={waitingByInput}
        selected={selection}
        onSelect={setSelection}
        drafts={otherDrafts}
        onContinueDraft={continueDraft}
        onDeleteDraft={deleteOtherDraft}
        onOpenPicker={() => setPickerOpen(true)}
        onSwitchInput={(input) => applyWorkflow({ input })}
        onImportSheet={() => setSheetOpen(true)}
        onLeave={saveAndClose}
      />
    )
  } else if (count === 1) {
    const sample = rows[0]
    samplesArea = (
      <>
        <SampleEditor
          sample={sample}
          workflow={workflow}
          fileMap={fileMap}
          onChange={(patch) => changeSample(sample.uid, patch)}
          onRemove={() => removeSamples([sample.uid])}
          onAddAnother={() => setPickerOpen(true)}
          issues={(issuesByUid.get(sample.uid) || []).filter((i) => i.code !== 'normal' || !tn)}
          onFix={(fixName, issue) => fix(sample.uid, fixName, issue)}
          normalOptions={normalOptionsFor(sample)}
          bedFiles={bedFiles}
          rnaCapture={rnaCapture}
        />
        {workflowValid && <SampleCredits workflow={workflow} sample={sample} prices={prices} sx={{ mt: 4 }} />}
      </>
    )
  } else {
    samplesArea = (
      <SampleTable
        rows={rows}
        workflow={workflow}
        fileMap={fileMap}
        prices={prices}
        issuesByUid={issuesByUid}
        onChangeSample={changeSample}
        onBulk={bulk}
        onRemove={removeSamples}
        onFix={fix}
        onMerge={mergeRows}
        normalOptionsFor={normalOptionsFor}
        bedFiles={bedFiles}
        rnaCapture={rnaCapture}
        onImportSheet={() => setSheetOpen(true)}
        onAddFiles={() => setPickerOpen(true)}
        expandedUid={expandedUid}
        onExpand={setExpandedUid}
        sheetNote={
          sheetNote ? (
            <Muted>
              {sheetNote}{' '}
              <TextLink tone="muted" onClick={() => setSheetNote(null)}>
                Dismiss
              </TextLink>
            </Muted>
          ) : null
        }
      />
    )
  }

  let body = null
  if (activeView !== 'compose') {
    if (groupMode) body = flow.body
    else if (view === 'review') {
      body = (
        <ReviewView
          workflow={workflow}
          startable={startable}
          excluded={excluded}
          warnings={reviewWarnings(workflow, startable)}
          price={price}
          balance={balance}
          name={name}
          namePlaceholder={autoBatchName(workflow)}
          onNameChange={setName}
          setAside={setAside}
        />
      )
    } else if (run) body = <StartView run={run} waiting={waiting} />
  }

  const otherTypesLink = FEATURES.otherAnalysisTypesLink

  return (
    <Box
      ref={panelRef}
      onKeyDown={onKeyDown}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 64, flexShrink: 0, px: { xs: 2, md: 4 } }}>
        {headerTitle ? (
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600 }} noWrap>
            {headerTitle}
          </Typography>
        ) : (
          <InputBase
            {...headerName}
            placeholder="New analysis"
            inputProps={{ 'aria-label': 'Batch name', maxLength: 120 }}
            sx={{
              fontSize: 18,
              fontWeight: 600,
              width: 360,
              maxWidth: '45%',
              borderRadius: `${theme.shape.borderRadius}px`,
              px: 1,
              mx: -1,
              '&:hover': { backgroundColor: 'action.hover' },
              '&.Mui-focused': { backgroundColor: 'action.hover' },
              '& input::placeholder': { color: 'text.primary', opacity: 1 },
            }}
          />
        )}
        <Muted sx={{ fontSize: 13.5 }}>{headerCount}</Muted>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={requestClose} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      {activeView === 'compose' ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            px: { xs: 2, md: 4 },
            pt: 1,
            overflowY: { xs: 'auto', md: 'hidden' },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 384 },
              flexShrink: 0,
              overflowY: { xs: 'visible', md: 'auto' },
              pr: { md: 1 },
              ...quietScrollSx(theme),
            }}
          >
            <WorkflowColumn
              workflow={workflow}
              onChange={changeWorkflow}
              rememberedHint={rememberedHint}
              samples={rows}
              sampleCount={rows.length}
              bedFiles={bedFiles}
              errors={errors}
              showErrors={showErrors}
              waitingByInput={waitingByInput}
              longReadStats={longReadStats}
              priceText={columnPriceText}
              onOpenOther={otherTypesLink ? onOpenOtherTypes || (() => setOtherOpen(true)) : undefined}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: { xs: 'visible', md: 'auto' },
              overflowX: 'auto',
              pb: 2,
              ...quietScrollSx(theme),
            }}
          >
            {/* Aligned with the list's rows while empty, with the editor or table after. */}
            {!groupMode && !sheetOpen && (
              <SetAsideNote groups={setAside} onStart={startSetAside} sx={count ? { px: 0 } : undefined} />
            )}
            {samplesArea}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 4 } }}>{body}</Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 72,
          flexShrink: 0,
          px: { xs: 2, md: 4 },
          py: 1.5,
          mt: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{footerText}</Box>
        {canReset && !sheetOpen && !starting && (
          <ResultButton onClick={resetPanel} sx={SOFT_BUTTON_SX}>
            Reset
          </ResultButton>
        )}
        {secondary && (
          <ResultButton onClick={secondary.onClick} sx={SOFT_BUTTON_SX}>
            {secondary.label}
          </ResultButton>
        )}
        {primary && (
          <ResultButton
            primary
            disabled={primary.disabled}
            onClick={primary.onClick}
            onKeyDown={ignoreHeldEnter}
            sx={{ ...SOFT_BUTTON_SX, gap: 1 }}
          >
            {primary.busy && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
            {primary.label}
          </ResultButton>
        )}
      </Box>

      <FilePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        candidates={candidates}
        analysed={analysed}
        inBatch={pickedIds}
        workflow={workflow}
        resolveWorkflow={resolvePickWorkflow}
        initialType={FILE_TYPE_OF_INPUT[workflow.input] || 'all'}
        onAdd={(cands) => {
          addSamples(cands)
          setPickerOpen(false)
        }}
      />
      {otherTypesLink && !onOpenOtherTypes && (
        <OtherAnalysisTypesDialog open={otherOpen} onClose={() => setOtherOpen(false)} onStarted={refreshAfterStart} />
      )}

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Starts are still running</DialogTitle>
        <DialogContent>
          <DialogContentText>Closing stops after the start being sent now. The rest do not start.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep open</Button>
          <Button
            color="error"
            onClick={() => {
              if (abortRef.current) abortRef.current.abort()
              flow.stop()
              setConfirmClose(false)
              onClose()
            }}
          >
            Close anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Warnings in the review; they do not block.
const reviewWarnings = (workflow, startable) => {
  const out = []
  if (workflow.analysis === 'germline') {
    // Who runs AI is not a warning: the review names it ("12 with AI · 24 without"),
    // and a sample set to AI without HPO terms and history does not start at all.
    const noHpo = startable.filter((s) => !(s.hpo || []).length).length
    if (noHpo) out.push(`${pluralSamples(noHpo)} ${noHpo === 1 ? 'has' : 'have'} no HPO terms.`)
  } else if (!workflow.tumourType) {
    const noType = startable.filter((s) => !s.tumourType).length
    if (noType) out.push(`${pluralSamples(noType)} ${noType === 1 ? 'has' : 'have'} no tumour type. They run untyped.`)
  }
  const keys = startable.map((s) => String(s.key).toLowerCase())
  const shared = keys.filter((k) => keys.indexOf(k) !== keys.lastIndexOf(k)).length
  if (shared) out.push(`${pluralSamples(shared)} share a name with another sample.`)
  if (workflow.input === 'pacbio') {
    // Methylation asked for (by the batch, or a sample's own options) on a file
    // without methylation data.
    const wantsMeth = (s) => (typeof s.longRead?.meth === 'boolean' ? s.longRead.meth : !!workflow.lrMeth)
    const noMeth = startable.filter((s) => wantsMeth(s) && !s.methylationReady).length
    if (noMeth) out.push(`Methylation is skipped for ${pluralSamples(noMeth)} without methylation data.`)
  }
  return out
}

const NewAnalysisDialog = ({
  open,
  onClose,
  onStarted,
  initialFiles,
  initialSamples,
  initialWorkflow,
  initialName,
  onOpenOtherTypes,
}) => {
  const theme = useTheme()
  const guardRef = useRef(null)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const openRef = useRef(open)
  openRef.current = open
  // Continue on a draft reopens the panel on it (a new key); Reset reopens it empty,
  // without the caller's files, samples or preset. The dialog's content unmounts once
  // closed, so the next opening is fresh; both are dropped then.
  const [session, setSession] = useState({ key: 0, draftId: null, reset: false })
  useEffect(() => {
    if (!open) setSession((prev) => (prev.draftId || prev.reset ? { ...prev, draftId: null, reset: false } : prev))
  }, [open])
  const resetPanel = (snapshot, userKey) => {
    setSession((prev) => ({ key: prev.key + 1, draftId: null, reset: true }))
    enqueueSnackbar('Panel cleared', {
      variant: 'info',
      autoHideDuration: 8000,
      action: snapshot
        ? (key) => (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                closeSnackbar(key)
                if (!restoreDraft(snapshot, userKey)) {
                  enqueueSnackbar('Could not bring it back', { variant: 'warning' })
                  return
                }
                // Back in the panel while it is open, else in the Drafts list.
                if (openRef.current) setSession((prev) => ({ key: prev.key + 1, draftId: snapshot.id, reset: true }))
              }}
              sx={{ fontWeight: 600, minWidth: 0 }}
            >
              Undo
            </Button>
          )
        : undefined,
    })
  }
  // A stray click outside never closes the panel; the X and Esc do (and save).
  const requestClose = (_event, reason) => {
    if (reason === 'backdropClick') return
    if (guardRef.current) guardRef.current()
    else onClose()
  }
  return (
    <Dialog
      open={!!open}
      onClose={requestClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: { xs: 2, lg: 3 },
          width: { xs: 'calc(100% - 32px)', lg: 'calc(100% - 48px)' },
          maxWidth: 1600,
          height: { xs: 'calc(100% - 32px)', lg: 'calc(100% - 48px)' },
          maxHeight: 'none',
          borderRadius: '12px',
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          overflow: 'hidden',
        },
      }}
    >
      <NewAnalysisPanel
        key={session.key}
        onClose={onClose}
        onStarted={onStarted}
        initialFiles={session.reset ? undefined : initialFiles}
        initialSamples={session.reset ? undefined : initialSamples}
        initialWorkflow={session.reset ? undefined : initialWorkflow}
        initialName={session.reset ? undefined : initialName}
        onOpenOtherTypes={onOpenOtherTypes}
        guardRef={guardRef}
        continueDraftId={session.draftId}
        onContinueDraft={(draftId) => setSession((prev) => ({ ...prev, key: prev.key + 1, draftId }))}
        onReset={resetPanel}
      />
    </Dialog>
  )
}

export default NewAnalysisDialog
